import { ExclamationCircleFilled } from "@ant-design/icons";
import { Alert, Button, Form, Input, Modal, Select, Switch } from "antd";
import _ from "lodash";
import React, { useMemo, useState } from "react";
import { useNotification } from "../../contexts/Notifications";
import { useCreateNewProjectMutation } from "../../generated/types";
import { namesRoughlyMatch } from "../OpenSpace/nameSimilarity";
import { REGION_OPTIONS } from "../OpenSpace/regions";
import { useOpenSpaceOrgSites } from "../OpenSpace/useOpenSpaceOrgSites";
import { Project } from "./ProjectsTableContainer";

const ADD_NEW_PROJECT_TXT = "Add New Project";
const PROJECT_NAME_FORM_FIELD = "project";
const CUSTOMER_NAME_FORM_FIELD = "customer";
const DISPLAY_NAME_FORM_FIELD = "displayName";
const DEFAULT_LANG_FORM_FIELD = "defaultLanguage";
const IS_OPENSPACE_PROJECT_FORM_FIELD = "isOpenSpaceProject";
const IS_GCP_FORM_FIELD = "isGcp";
const OS_REGION_FORM_FIELD = "osRegion";
const OS_ORGANIZATION_FORM_FIELD = "osOrganization";
const OS_SITE_FORM_FIELD = "osSite";

const defaultLanguageOpts = ["en-gb", "en-us"];

const ALERT_MSG = (
  <ul>
    <li>
      You require the <b>Delivery Core Project Creation</b> permission in Janus
      to perform this action
    </li>
    <li>
      This will create the project in delivery-core and create the relevant
      janus permissions.
    </li>
    <li>
      If you need to create a new customer, put a request in{" "}
      <b>#sync-new-projects-request</b>
    </li>
    <li>
      IMPORTANT: You will need to add the following newly created janus policies
      to the correct groups:
      <ul>
        <li>
          <b>Internal [customer] [project] (Delivery Core)</b> to <b>DAPS</b>{" "}
          and <b>Architects (Delivery Core)</b>
        </li>
        <li>
          <b>View Internal Impulse Progress Reports [customer] [project]</b> to{" "}
          <b>DAPS</b> and <b>Architects (Delivery Core)</b>
        </li>
      </ul>
    </li>
  </ul>
);

type Props = {
  projects: Project[];
};

export const NewProjectButton = (props: Props) => {
  const { projects } = props;
  const notify = useNotification();
  const [modalVisible, setModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(true);
  const [form] = Form.useForm();
  const [modalApi, modalContextHolder] = Modal.useModal();
  const isOpenSpaceProject = Form.useWatch(
    IS_OPENSPACE_PROJECT_FORM_FIELD,
    form,
  );
  const displayName = Form.useWatch(DISPLAY_NAME_FORM_FIELD, form);
  const selectedOsRegion = Form.useWatch(OS_REGION_FORM_FIELD, form);
  const selectedOsOrganization = Form.useWatch(
    OS_ORGANIZATION_FORM_FIELD,
    form,
  );
  const selectedOsSite = Form.useWatch(OS_SITE_FORM_FIELD, form);
  const openSpace = useOpenSpaceOrgSites();

  const selectedOsSiteName = openSpace.sites.find(
    (site) => site.id === selectedOsSite,
  )?.name;
  // undefined until both a site and a display name exist; then whether the
  // Track display name fuzzy-matches the OpenSpace site name.
  const siteNameMatches =
    selectedOsSiteName === undefined || !displayName
      ? undefined
      : namesRoughlyMatch(displayName, selectedOsSiteName);

  const [createNewProject] = useCreateNewProjectMutation({
    onCompleted: () => notify("Successfully created new project", "success"),
    onError: (err) =>
      notify(`Error creating new project: ${err.message}`, "error"),
    update: (cache) => {
      cache.evict({ fieldName: "projects" }); // Triggers refetch of projects.
    },
  });

  const handleOk = async () => {
    try {
      await form.validateFields();
    } catch {
      // Validation failed - form will show errors
      return;
    }
    if (isOpenSpaceProject && siteNameMatches === false) {
      modalApi.confirm({
        title: "Warning: these project names do not match",
        icon: <ExclamationCircleFilled style={{ color: "#cf1322" }} />,
        content: (
          <>
            The Track project is named <strong>{displayName}</strong>, but the
            selected OpenSpace site is <strong>{selectedOsSiteName}</strong>.
            Linking the wrong site can expose a client&apos;s data to
            unauthorized users outside their organization. Only continue if you
            are certain these are the same project.
          </>
        ),
        okText: "Link anyway",
        okButtonProps: { danger: true },
        cancelText: "Cancel",
        // Only an explicit answer closes the dialog; Esc is disabled (and the
        // mask doesn't close confirms) so a dismissal can't be mistaken for
        // "Cancel" and silently clear the selection.
        keyboard: false,
        onOk: submitProject,
        onCancel: handleMismatchNo,
      });
      return;
    }
    await submitProject();
  };

  const submitProject = async () => {
    try {
      setIsSubmitting(true);
      const result = await createNewProject({
        variables: {
          customer: form.getFieldValue(CUSTOMER_NAME_FORM_FIELD),
          project: form.getFieldValue(PROJECT_NAME_FORM_FIELD),
          defaultLanguage: form.getFieldValue(DEFAULT_LANG_FORM_FIELD),
          displayName: form.getFieldValue(DISPLAY_NAME_FORM_FIELD),
          openSpace: form.getFieldValue(IS_OPENSPACE_PROJECT_FORM_FIELD)
            ? {
                region: form.getFieldValue(OS_REGION_FORM_FIELD),
                organizationId: form.getFieldValue(OS_ORGANIZATION_FORM_FIELD),
                siteId: form.getFieldValue(OS_SITE_FORM_FIELD),
              }
            : null,
          isGcp: form.getFieldValue(IS_GCP_FORM_FIELD) ?? false,
        },
      });
      setIsSubmitting(false);
      // With onError set, a failed mutation resolves (with errors) instead of
      // throwing — keep the modal open so the user's input isn't lost.
      if (result.errors) {
        return;
      }
      setModalVisible(false);
      form.resetFields();
      openSpace.reset();
      setHasValidationErrors(true);
    } catch {
      // Mutation error is already handled by onError callback
      setIsSubmitting(false);
    }
  };

  const handleMismatchNo = () => {
    form.setFieldsValue({ [OS_SITE_FORM_FIELD]: undefined });
    checkValidationErrors();
  };

  const handleCancel = () => {
    form.resetFields();
    openSpace.reset();
    setModalVisible(false);
    setHasValidationErrors(true);
  };

  const handleOsRegionChange = (region: string) => {
    form.setFieldsValue({
      [OS_ORGANIZATION_FORM_FIELD]: undefined,
      [OS_SITE_FORM_FIELD]: undefined,
    });
    // No customer/project: the project doesn't exist yet, so the mapping
    // service authenticates this listing with the Janus bot token instead
    // of a per-project token.
    openSpace.fetchOrganizations(region);
  };

  const handleOsOrganizationChange = (organizationId: string) => {
    form.setFieldsValue({ [OS_SITE_FORM_FIELD]: undefined });
    openSpace.fetchSites(
      organizationId,
      form.getFieldValue(OS_REGION_FORM_FIELD),
    );
  };

  const checkValidationErrors = useMemo(
    () =>
      _.debounce(async () => {
        try {
          await form.validateFields();
          setHasValidationErrors(false);
        } catch {
          setHasValidationErrors(true);
        }
      }, 300),
    [form],
  );

  const validateProjectNameLength = (_rule, value: string) => {
    if (!value) {
      return Promise.resolve();
    }
    const customerName = form.getFieldValue(CUSTOMER_NAME_FORM_FIELD);
    if (!customerName) {
      return Promise.resolve(); // Will be caught by required validation
    }
    const maxLength = 50 - customerName.length;
    if (value.length > maxLength) {
      return Promise.reject(
        `Project name cannot exceed ${maxLength} characters (50 minus customer name length)`,
      );
    }
    return Promise.resolve();
  };

  const validateProjectName = (_rule, value: string) => {
    const existingProjectNames = projects.map((p) => p.project);
    if (!value) {
      return Promise.resolve();
    }
    if (existingProjectNames.includes(value)) {
      return Promise.reject(
        "Project name already exists. Please choose a different name.",
      );
    }
    const regex = /^[a-z0-9]+$/;
    if (regex.test(value)) {
      return Promise.resolve();
    }
    return Promise.reject(
      "Project name should include only lowercase letters and numbers.",
    );
  };

  const customerOptions = useMemo(
    () =>
      mapOptions(
        _(projects)
          .map((p) => p.customer)
          .uniq()
          .value(),
      ),
    [projects],
  );

  return (
    <>
      <Button
        type="primary"
        onClick={() => {
          setModalVisible(true);
          setHasValidationErrors(true);
        }}
      >
        {ADD_NEW_PROJECT_TXT}
      </Button>
      <Modal
        visible={modalVisible}
        title={ADD_NEW_PROJECT_TXT}
        onCancel={handleCancel}
        onOk={handleOk}
        confirmLoading={isSubmitting}
        okText={"Create"}
        okButtonProps={{ disabled: hasValidationErrors }}
      >
        <>
          <Alert
            style={{ marginBottom: "15px" }}
            type="info"
            showIcon
            message={ALERT_MSG}
          />
          <Form
            form={form}
            layout="vertical"
            onValuesChange={checkValidationErrors}
          >
            <Form.Item
              name={CUSTOMER_NAME_FORM_FIELD}
              label="Customer"
              rules={[
                {
                  required: true,
                  message: "Please select a customer",
                },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select a customer"
                options={customerOptions}
              />
            </Form.Item>
            <Form.Item
              name={PROJECT_NAME_FORM_FIELD}
              label="Project URL Name"
              dependencies={[CUSTOMER_NAME_FORM_FIELD]}
              rules={[
                {
                  required: true,
                  message: "Please enter the project name",
                },
                {
                  validator: validateProjectNameLength,
                },
                {
                  validator: validateProjectName,
                },
              ]}
            >
              <Input placeholder="Enter project name" />
            </Form.Item>
            <Form.Item
              name={DISPLAY_NAME_FORM_FIELD}
              label="Project Display Name"
              rules={[
                {
                  required: true,
                  message: "Please enter the display name",
                },
              ]}
            >
              <Input placeholder="Enter display name" />
            </Form.Item>
            <Form.Item
              name={DEFAULT_LANG_FORM_FIELD}
              label="Default Language"
              rules={[
                {
                  required: true,
                  message: "Please select the default language",
                },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select default language"
                options={mapOptions(defaultLanguageOpts)}
              />
            </Form.Item>
            <Form.Item
              name={IS_OPENSPACE_PROJECT_FORM_FIELD}
              label="Openspace Project"
              valuePropName="checked"
              initialValue={false}
            >
              <Switch />
            </Form.Item>
            {isOpenSpaceProject && (
              <>
                {openSpace.error && (
                  <Alert
                    style={{ marginBottom: "15px" }}
                    type="error"
                    showIcon
                    message={openSpace.error}
                  />
                )}
                <Form.Item
                  name={OS_REGION_FORM_FIELD}
                  label="Openspace Region"
                  rules={[
                    {
                      required: true,
                      message: "Please select a region",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select a region"
                    options={REGION_OPTIONS}
                    onChange={handleOsRegionChange}
                  />
                </Form.Item>
                <Form.Item
                  name={OS_ORGANIZATION_FORM_FIELD}
                  label="Client in Openspace"
                  rules={[
                    {
                      required: true,
                      message: "Please select a client",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select a client"
                    loading={openSpace.orgLoading}
                    disabled={!selectedOsRegion}
                    options={openSpace.organizations.map((org) => ({
                      label: org.name,
                      value: org.id,
                    }))}
                    onChange={handleOsOrganizationChange}
                  />
                </Form.Item>
                <Form.Item
                  name={OS_SITE_FORM_FIELD}
                  label="Project in Openspace"
                  hasFeedback={siteNameMatches !== undefined}
                  validateStatus={
                    // No site picked yet: defer to antd's automatic status so
                    // the required-rule error still shows. Site picked but no
                    // display name to compare: force neutral ("") — antd would
                    // otherwise mark the satisfied required rule green.
                    selectedOsSiteName === undefined
                      ? undefined
                      : siteNameMatches === undefined
                      ? ""
                      : siteNameMatches
                      ? "success"
                      : "warning"
                  }
                  help={
                    siteNameMatches === false
                      ? "This name doesn't match the Track project name — linking the wrong site can expose client data outside the organization."
                      : undefined
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please select a project",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select a project"
                    loading={openSpace.siteLoading}
                    disabled={!selectedOsOrganization}
                    options={openSpace.sites.map((site) => ({
                      label: site.name,
                      value: site.id,
                    }))}
                  />
                </Form.Item>
              </>
            )}
            <Form.Item
              name={IS_GCP_FORM_FIELD}
              label="GCP Project"
              valuePropName="checked"
              initialValue={false}
            >
              <Switch />
            </Form.Item>
          </Form>
        </>
      </Modal>
      {modalContextHolder}
    </>
  );
};

const mapOptions = <T,>(arr: T[]): { label: T; value: T }[] =>
  arr.map((el) => ({ label: el, value: el }));
